import { useState } from "react";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { clinicConfig } from "@/config/clinic";

const CARE_TYPES = ["In-person visit", "Virtual care (Doc Talk)", "Prescription refill", "Something else"];

export const AppointmentModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ name: "", phone: "", type: CARE_TYPES[0], note: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success("Taking you to Doc Talk", {
      description: "Finish signing in to confirm your request securely.",
    });
    onOpenChange(false);
    setTimeout(() => {
      window.open(clinicConfig.patientPortalUrl, "_blank", "noopener,noreferrer");
    }, 400);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="appointment-modal" className="max-w-lg border-border bg-background">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-medium tracking-tight text-foreground">
            Request care
          </DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">
            Tell us a little about what you need. We'll route you to Doc Talk to
            sign in securely and confirm.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-2 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="ap-name" className="text-sm font-medium text-foreground">Full name</Label>
            <Input
              id="ap-name"
              data-testid="appt-name-input"
              required
              value={form.name}
              onChange={update("name")}
              placeholder="Jane Doe"
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ap-phone" className="text-sm font-medium text-foreground">Phone number</Label>
            <Input
              id="ap-phone"
              data-testid="appt-phone-input"
              type="tel"
              required
              value={form.phone}
              onChange={update("phone")}
              placeholder="(828) 000-0000"
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ap-type" className="text-sm font-medium text-foreground">What do you need?</Label>
            <select
              id="ap-type"
              data-testid="appt-type-select"
              value={form.type}
              onChange={update("type")}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {CARE_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ap-note" className="text-sm font-medium text-foreground">Anything else? (optional)</Label>
            <Textarea
              id="ap-note"
              data-testid="appt-note-input"
              value={form.note}
              onChange={update("note")}
              placeholder="A short note about your visit"
              rows={3}
            />
          </div>

          <button
            type="submit"
            data-testid="appt-submit-btn"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-4 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Continue to Doc Talk
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
          </button>
          <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-accent" strokeWidth={1.5} />
            Your sign-in and records stay secure on Doc Talk.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentModal;
